/**
 * Admin — App Access panel
 *
 * Sits at the top of User Management, next to Sign-in Methods. Currently one
 * control: block the logged-in app on phone-sized screens (system setting
 * `ui.block_small_devices`). The public landing/login pages are never affected.
 *
 * System-admin UI: English only, light-theme palette (reuses the Sign-in
 * Methods panel styles for a consistent look).
 */

import React, { useEffect, useState } from 'react';
import { getSystemSetting, putSystemSetting, SETTING_KEYS } from '../../api/systemSettings';
import styles from './SignInMethodsPanel.module.css';

function Toggle({
  on,
  busy,
  onClick,
  ariaLabel,
}: {
  on: boolean;
  busy?: boolean;
  onClick?: () => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={ariaLabel}
      disabled={busy}
      onClick={onClick}
      className={`${styles.toggle} ${on ? styles.toggleOn : ''}`}
    >
      <span className={styles.knob} />
    </button>
  );
}

export default function AppAccessPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockSmall, setBlockSmall] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const v = await getSystemSetting(SETTING_KEYS.BLOCK_SMALL_DEVICES);
        if (!cancelled) setBlockSmall(v === 'true');
      } catch {
        if (!cancelled) setError('Could not load app-access settings.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = async () => {
    const next = !blockSmall;
    setError(null);
    setSaving(true);
    setBlockSmall(next); // optimistic
    try {
      await putSystemSetting(SETTING_KEYS.BLOCK_SMALL_DEVICES, next ? 'true' : 'false');
    } catch {
      setBlockSmall(!next); // revert
      setError('Could not save that change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className={styles.card} aria-label="App access">
      <div className={styles.cardHead}>
        <div>
          <h3 className={styles.cardTitle}>App Access</h3>
          <p className={styles.cardSubtitle}>Control where the app can be used after login.</p>
        </div>
      </div>

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : (
        <div className={styles.rows}>
          <div className={styles.row}>
            <div className={styles.rowText}>
              <span className={styles.rowLabel}>Block app on small screens</span>
              <span className={styles.rowDesc}>
                When on, phones see a &ldquo;use a larger screen&rdquo; message after login. The
                landing and login pages still work on any device.
              </span>
            </div>
            <Toggle
              on={blockSmall}
              busy={saving}
              onClick={toggle}
              ariaLabel="Toggle blocking the app on small screens"
            />
          </div>
        </div>
      )}

      {error && <div className={styles.error}>{error}</div>}
    </section>
  );
}
